import { getLocales } from 'assets/locales/Locale';
import { getPreviousAppLocation } from 'assets/hooks/pageRouter/AppHistory';
import { useAuthStore } from 'assets/providers/authStore/Provider.AuthStore';
import ProviderBuilder from 'assets/providers/ProviderBuilder';
import { compact } from 'lodash';
import usePublicClaimSubmit from 'module/public-web/views/claimSubmit/Claim.Submit.Hooks';
import { useState } from 'react';
import { useHistory, useLocation } from 'react-router';
import { ClaimCreateNavigationState } from 'module/claims/helper/Claim.CreateNavigation';
import {
  canCreateClaimForContractStatus,
  isClaimContractExpired,
  isClaimContractExpiredWindowExceeded,
} from 'module/claims/helper/Claim.ContractEligibility';

export type PublicClaimForm = Module.Claims.Claim.Submit.Form & {
  uploadableClaimTypeDocuments: Model.IClaimTypeDocument[];
};

function usePublicCreateClaimCore() {
  const history = useHistory();
  const location = useLocation<ClaimCreateNavigationState>();
  const { user, permissions } = useAuthStore();
  const candidate = location.state?.createForContract;
  const canCreateForContract =
    candidate &&
    user &&
    canCreateClaimForContractStatus(candidate.contract) &&
    !isClaimContractExpiredWindowExceeded(candidate.contract) &&
    (permissions?.CLAIM_ADMIN ||
      (isClaimContractExpired(candidate.contract)
        ? permissions?.CREATE_SIMPLE_CLAIM && permissions?.CREATE_CLAIM_FOR_EXPIRED_CONTRACT
        : permissions?.CREATE_CLAIM));
  const [initialClaim] = useState(() => (canCreateForContract ? candidate : undefined));
  const [returnLocation] = useState(getPreviousAppLocation);
  const _original = usePublicClaimSubmit({
    initialClaim,
    onSaved: () => {
      if (returnLocation) history.replace(returnLocation);
      else setCurrentStep(6);
    },
  });
  const { lang } = getLocales();
  const steps = compact([
    { id: 1, title: lang.contract },
    { id: 2, title: lang.claimInfoTitle },
    user ? null : { id: 3, title: lang.clientInfo },
    { id: 4, title: lang.addJob },
    { id: 5, title: lang.publicClaimReview },
    { id: 6, title: lang.finish },
  ]);
  const [currentStep, _setCurrentStep] = useState(1);
  function setCurrentStep(step: number) {
    _setCurrentStep(user && step === 3 ? 4 : step);
  }

  const isCreateClaimStepValidated =
    (!initialClaim || !!_original.claimTypesListPublicApi.payload) &&
    !_original.form.errors.claimCreationOption &&
    !_original.form.errors.claim?.contract?._objectError &&
    !_original.form.errors.newPayee &&
    !_original.form.errors.repairFacilityId &&
    !_original.form.errors.claim?.dealerId;

  const isClaimInfoStepValidated =
    !_original.form.errors.claim?.odometerReading && !_original.form.errors.claim?.workOrderNumber;

  const isClientInfoStepValidated = !_original.form.errors.claim?.createdByPayee;

  const isAddJobStepValidated =
    !_original.form.errors.claim?.claimJobs?._objectError && !_original.form.errors.claim?.claimJobs?.claimTypeId;

  const validatedSteps = [
    isCreateClaimStepValidated,
    isClaimInfoStepValidated,
    isClientInfoStepValidated,
    isAddJobStepValidated,
    false,
    false,
  ];
  const isCurrentStepValidated = !!validatedSteps[currentStep - 1];
  function getResetClaimData() {
    return {
      ..._original.form.data.claim,
      dealerId: null,
      odometerReading: undefined,
      workOrderNumber: undefined,
      createdByPayee: {
        phoneNumber: undefined,
        email: undefined,
        name: undefined,
        payeeTypeId: undefined,
      },
      claimDocuments: [],
      claimJobs: _original.form.data.claim?.claimJobs?.map((it) => ({
        ...it,
        claimJobItems: [],
        claimJobDocuments: [],
        dataPoints: [],
        claimType: undefined,
        claimTypeId: undefined,
        customerComplaint: undefined,
        correction: undefined,
        cause: undefined,
      })),
    };
  }

  return {
    ..._original,
    isContractCreation: !!initialClaim,
    steps,
    isCurrentStepValidated,
    setCurrentStep,
    currentStep,
    getResetClaimData,
  };
}

const { useProvider: usePublicCreateClaimProvider, Provider: PublicCreateClaimProvider } =
  ProviderBuilder(usePublicCreateClaimCore);

export { PublicCreateClaimProvider, usePublicCreateClaimProvider };
